import React, {
    FC,
    useContext, useCallback,
    useMemo,
    useState,
    useEffect,
    useRef,
} from 'react';
import { gql, useFragment } from '@apollo/client';
import { tp } from 'app/utils/typograf';

import { MenuDispatchContext, MenuDataContext } from 'app/containers/contexts/menu.context';
import { DishesSelectionContext } from 'app/containers/contexts/dishesSelection.context';
import { LocaleContext } from 'app/containers/LocaleProvider';

import {
    locales as umAliasLocales,
    PORTION_UM_ALIAS_TYPE,
} from 'app/const/umAlias';
import { ADDITIONAL_DISHES_CATEGORIES } from 'app/const/categories';
import { KZN_CODES, NIZ_CODES, SPB_CODES } from 'app/const/location';

import {
    createShelfLifeData, formatCookUntil, getImageUrl, menuImageSize,
    menuImageSizeMobile,
    SOLD_OUT_STATUS,
} from 'app/utils/dish';
import pluralize from 'app/utils/pluralize';
import { addBrTag } from 'app/utils/addBrTag';

import { ADDRESS_SUBDIVISION_FRAGMENT } from 'app/graphql/address/subdivision.fragment.graphql';

import { CustomIngredientsSelectDishRow } from 'app/views/Menu/Menu/components/CustomIngredientsSelectDishRow';
import { Portal } from 'react-portal';
import classNames from 'classnames';
import { BOXES_IDS_COLLECTION } from 'app/const/dishesWithBoxes';
import { DishImageShield } from 'app/components/ui/Elements/DishImageShield/DishImageShield';
import { analyticService } from 'global/services';
import { RENDERING_CONTEXT_MENU, useDishDetails } from '../../MobileDishDetails';


import css from './DishCardRegular.module.scss';
import { PortionsButton } from '../../ui/BR1258/PortionsButton/PortionsButton';
import { TabBar } from '../../ui/BR1258/TabBar/TabBar';
import { TagsShields } from '../../ui/BR1258/TagsShields/TagsShields';
import MobileDishImageFC from '../../MobileDish/MobileDishImage';
import { DialogStaticNewUI } from '../../Dialog/BR1258/DialogStatic';

import PanIcon from './img/pan.component.svg';
import CrossIcon from './img/cross.component.svg';
// @ts-ignore
import stubImg from './img/stub.jpg';

const locales: any = {
    ru: {
        weight: 'г',
        min: 'мин',
        ready: 'Уже готово',
    },
    en: {
        weight: 'g',
        min: 'min',
        ready: 'Уже готово',
    },
};

const highlightText = (text: string, words: string[], callback: () => void): any => {
    if (!words || words.length === 0) return text;

    const regex = new RegExp(`(${words.join('|')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part) => (words.some((word) => word.toLowerCase() === part.toLowerCase())
        ? (
            <span
                className={css.highlightedWord}
                onClick={((e: any) => {
                    e.stopPropagation();
                    callback();
                })}
                role="none"
                key={part}
            >
                {part}
            </span>
        )
        : part),
    );
};

function fixDanglingPrepositions(title: string, caption: string): { title: string, caption: string } {
    const prepositions = new Set([
        'и', 'с', 'со', 'в', 'во', 'на', 'по', 'к', 'ко', 'от', 'ото',
        'до', 'за', 'над', 'под', 'об', 'обо', 'о', 'при', 'через',
        'для', 'без', 'безо', 'из', 'изо', 'у', 'а', 'но', 'да',
        'или', 'же', 'ли', 'то', 'бы', 'ни', 'со', 'из-за', 'из-под',
    ]);

    const words = title.trim().split(/\s+/);

    if (words.length <= 1 || !caption.trim()) {
        return { title, caption };
    }

    const lastWordClean = words[words.length - 1].replace(/[.,!?;:]/g, '').toLowerCase();

    if (prepositions.has(lastWordClean)) {
        const lastWordOriginal = words[words.length - 1];
        const newTitle = words.slice(0, -1).join(' ');
        const newCaption = `${lastWordOriginal} ${caption.trim()}`;

        return { title: newTitle, caption: newCaption };
    }

    return { title, caption };
}

const dishesWithHiddenTime = ['5468', '5438', '5464', '5486', '5490'];

export const DishCardRegular: FC = (props: any) => {
    const {
        id,
        nutrition,
        categoryId,
        portions,
        selectedDishPortions,
        price,
        previewMobileImage,
        onOpenDetails,
        spaDishObserver,
        umAlias = ['г', 'г', 'г', 'г'],
        isDishCardWithCustomSelect,
        isDishCardWithCustomTabBar,
        isDishWithAdditionalDishesSelect,
        ingredientsList,
        upsellDishesList,
        selectedDishId,
        onNextIdSelected,
        optionTags,
        additionalDescription,
        previewImage,
        useLazyLoading,
        highlightWords,
        highlightDescription,
        highlightDescriptionTitle,
        highlightImage,
        canApplySoldOutStyles = false,
        scrollPosition = null,
        visibleStatus,
        isLastDish,
        isAutoscrollingDisabled = false,
        titleEn = '',
        captionEn = '',
        ...restProps
    } = props;

    const rootRef = useRef(null);
    const [isNeededRenderStub, setIsNeededRenderStub] = useState(false);
    const [isDialogOpenned, setIsDialogOpenned] = useState(false);

    const { openDishDetails } = useDishDetails(() => { });
    const { locale } = useContext(LocaleContext);

    const { data: { country_subdivision: cs } } = useFragment({
        fragment: ADDRESS_SUBDIVISION_FRAGMENT,
        from: {
            __ref: 'address:session_address',
        },
    }) || 'RU-MOS';

    const { data: dishData } = useFragment({
        fragment: gql`fragment DISH_DATA on menuDish {
            title
            caption
            additionalDescription
            tags {
                id
            }
            cooking_time
            cook_until
            weight
            price
            option_tags
        }`,
        from: {
            __ref: `menuDish:${isDishWithAdditionalDishesSelect ? id : selectedDishId}`,
        },
    });

    const tagsString = useMemo(
        () => dishData?.tags?.map((tag: any) => tag.id)?.join(','),
        [dishData],
    );

    const isSoldOut = useMemo(
        () => canApplySoldOutStyles && visibleStatus === SOLD_OUT_STATUS,
        [canApplySoldOutStyles, visibleStatus],
    );

    const isBoxDish = BOXES_IDS_COLLECTION.includes(id);

    const currentAdditionalDescription = useMemo(
        () => dishData?.additionalDescription || additionalDescription,
        [dishData?.additionalDescription, additionalDescription],
    );

    const currentOptionTags = useMemo(
        () => (optionTags?.length ? optionTags : dishData?.option_tags),
        [optionTags, dishData],
    );

    // cooking time
    const cookingTimeInMin = Math.round(dishData.cooking_time / 60);

    const hasAlias = umAlias[0] !== 'г';
    const isAdditionalDish = ADDITIONAL_DISHES_CATEGORIES.includes(categoryId);
    const isRegularDish = !isAdditionalDish;

    // days
    const {
        untilTitle,
        untilDaysPrefix,
        untilDays,
        untilText,
    } = useMemo(() => {
        // const isShelfLife = ADDITIONAL_DISHES_CATEGORIES.includes(String(categoryId));

        const { number, days: cookUntilDays } = formatCookUntil(dishData.cook_until);
        const { days: shelfLifeDays } = createShelfLifeData(dishData.cook_until);

        return {
            // untilTitle: isShelfLife ? 'срок хранения' : 'готовить',
            // untilDaysPrefix: isShelfLife ? '' : 'на ',
            // untilText: isShelfLife ? shelfLifeDays : cookUntilDays,
            untilTitle: 'готовить',
            untilDaysPrefix: 'на ',
            untilText: cookUntilDays,

            untilDays: [...SPB_CODES, ...NIZ_CODES, ...KZN_CODES]
                .includes(cs) ? Number(number) - 1 : number,
        };
    },
        // categoryId
        [cs, dishData.cook_until],
    );

    const { title, caption } = useMemo(
        () => fixDanglingPrepositions(dishData?.title, dishData?.caption),
        [dishData],
    );
    // portion
    const [UM_ALIAS_TYPE] = umAlias || [PORTION_UM_ALIAS_TYPE];
    // @ts-ignore
    const portionAliases: any = umAliasLocales[locale][UM_ALIAS_TYPE] || umAliasLocales[locale][PORTION_UM_ALIAS_TYPE];
    const um = pluralize(portions[0], portionAliases);

    const handleOpenDetails = useCallback(() => {
        openDishDetails(
            isDishWithAdditionalDishesSelect && !isDishCardWithCustomTabBar && !isDishCardWithCustomSelect ? id : selectedDishId,
            RENDERING_CONTEXT_MENU,
            isDishWithAdditionalDishesSelect && !isDishCardWithCustomTabBar && !isDishCardWithCustomSelect
                ? {
                    selectedDishId,
                    selectedDishPortions: selectedDishPortions[0],
                }
                : undefined,
        );
        if (onOpenDetails) {
            onOpenDetails(id);
        }
    }, [
        id, openDishDetails, onOpenDetails,
        isDishWithAdditionalDishesSelect, isDishCardWithCustomTabBar, isDishCardWithCustomSelect,
        selectedDishId, selectedDishPortions,
    ]);

    const handleSendAnalytics = useCallback(
        () => {
            const dishesNodes = Array.from(document.querySelectorAll('[data-dish-id]'))
                .map((e) => e.getAttribute('data-dish-id'));
            analyticService
                .push({
                    eventName: 'Click_explanation',
                    params: {
                        dish_id: id,
                        dish_name: dishData.title,
                        dish_index: dishesNodes.indexOf(id) + 1,
                        price: dishData.price,
                        source_name: 'new_menu',
                    },
                });
        },
        [id, dishData],
    );

    const imageUrl = getImageUrl({ url: previewImage, ...menuImageSize });
    const imageUrlMobile = getImageUrl({ url: previewMobileImage, ...menuImageSizeMobile });

    const handleImageLoaded = useCallback(
        () => {
            setIsNeededRenderStub(false);
        },
        [],
    );

    const handleOpenDialog = useCallback(() => {
        setIsDialogOpenned(true);
        handleSendAnalytics();
    }, [handleSendAnalytics]);

    const rootClasses = classNames({
        [css.dishCardRegularRoot]: true,
        [css.last]: isLastDish,
        'shadow-xl': true,
    });

    const imageClasses = classNames({
        [css.dishCardRegularImg]: true,
        [css.large]: isBoxDish,
    });

    useEffect(
        () => {
            if (rootRef.current) {
                spaDishObserver.observe(rootRef.current);
            }
        },
        // eslint-disable-next-line
        [rootRef.current],
    );

    return (
        <>
            <div
                className={rootClasses}
                data-id={id}
                // параметр для рассчёта индекса блюда
                data-dish-id={id}
                onClick={handleOpenDetails}
                role="none"
                ref={rootRef}
            >
                <div className={imageClasses}>
                    {!isNeededRenderStub && (
                        <div className={css.dishCardRegularImgWrapper}>
                            <MobileDishImageFC
                                id={id}
                                url={imageUrlMobile}
                                fallbackUrl={imageUrl}
                                alt={title}
                                scrollPosition={scrollPosition}
                                useLazyLoading={useLazyLoading}
                                onLoadError={handleImageLoaded}
                                onLoad={handleImageLoaded}
                            />
                            {isSoldOut && (
                                <div className={css.dishCardRegularSoldOutBlur} />
                            )}
                        </div>
                    )}
                    {!isSoldOut && (
                        <div
                            className={css.dishCardRegularAddButtonWrapper}
                            onClick={(e) => e.stopPropagation()}
                            role="none"
                        >
                            <PortionsButton
                                portions={portions}
                                selectedDishPortions={selectedDishPortions}
                                price={dishData.price || price}
                                dishId={id}
                                selectedDishId={selectedDishId}
                                isDishWithAdditionalDishesSelect={isDishWithAdditionalDishesSelect
                                    && !isDishCardWithCustomTabBar && !isDishCardWithCustomSelect}
                            />
                        </div>
                    )}
                    <DishImageShield
                        dishId={id}
                        isSoldOut={isSoldOut}
                        tagsString={tagsString}
                    />
                </div>
                <div className={css.dishCardRegularInfoContainer}>
                    <div className={`body-xs-medium ${css.dishCardRegularAdditionalInfo}`}>
                        {cookingTimeInMin !== 0 && !dishesWithHiddenTime.includes(id) && (
                            <div
                                className={css.dishCardRegularPanIconWrapper}
                                style={{ position: 'relative', top: '-1px' }}
                            >
                                <PanIcon />
                            </div>
                        )}
                        {/* eslint-disable no-nested-ternary */}
                        {!dishesWithHiddenTime.includes(id) ? (
                            <div>
                                {cookingTimeInMin === 0 ? (
                                    isRegularDish
                                        ? locales[locale].ready
                                        : hasAlias
                                            ? (
                                                <>
                                                    <span className={`other-number-xs-regular ${css.dishCardRegularAdditionalInfoDigitLineValue}`}>
                                                        {portions[0]}
                                                    </span>
                                                    {' '}
                                                    <span className={css.dishCardRegularAdditionalInfoTextLineValue}>
                                                        {um}
                                                    </span>
                                                </>

                                            )
                                            : (
                                                <>
                                                    <span
                                                        className={`other-number-xs-regular
                                                            ${css.dishCardRegularAdditionalInfoDigitLineValue}`}
                                                    >
                                                        {cookingTimeInMin}
                                                    </span>
                                                    {' '}
                                                    <span className={css.dishCardRegularAdditionalInfoTextLineValue}>
                                                        {locales[locale].min}
                                                    </span>
                                                </>
                                            )
                                ) : (
                                    <>
                                        <span
                                            className={`other-number-xs-regular ${css.dishCardRegularAdditionalInfoDigitLineValue}`}
                                        >
                                            {cookingTimeInMin}
                                        </span>
                                        {' '}
                                        <span className={css.dishCardRegularAdditionalInfoTextLineValue}>
                                            {locales[locale].min}
                                        </span>
                                    </>
                                )}
                            </div>
                        ) : ''}
                        {!dishesWithHiddenTime.includes(id) && (
                            <>
                                {untilDays && (
                                    <>
                                        {cookingTimeInMin ? (
                                            <div className={css.dishCardRegularAdditionalInfoLineDivider}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="2" height="2" viewBox="0 0 2 2" fill="none">
                                                    <circle cx="1" cy="1" r="1" fill="#9C9C9C" />
                                                </svg>
                                            </div>
                                        ) : ''}
                                        <span className={css.dishCardRegularAdditionalInfoLineTitle}>
                                            {untilTitle}
                                        </span>
                                        <span className={css.dishCardRegularAdditionalInfoLineValueDays}>
                                            {untilDaysPrefix}
                                            <span className={`other-number-xs-regular ${css.dishCardRegularAdditionalInfoDigitLineValue}`}>
                                                {`1-${untilDays}`}
                                            </span>
                                            {' '}
                                            {untilText}
                                        </span>
                                    </>
                                )}
                                <div className={css.dishCardRegularAdditionalInfoLineDivider}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="2" height="2" viewBox="0 0 2 2" fill="none">
                                        <circle cx="1" cy="1" r="1" fill="#9C9C9C" />
                                    </svg>
                                </div>
                            </>
                        )}
                        <div className={css.flexWrapper}>
                            {!(cookingTimeInMin === 0 && !isRegularDish && hasAlias) && (
                                <>
                                    <span className={`other-number-xs-regular ${css.dishCardRegularAdditionalInfoLineValueDays}`}>
                                        {portions.length
                                            ? portions[0]
                                            : null}
                                    </span>
                                    <div className={css.crossIconWrapper}>
                                        <CrossIcon />
                                    </div>
                                </>
                            )}
                            <span className={`other-number-xs-regular ${css.dishCardRegularAdditionalInfoLineValueDays}`}>
                                {dishData.weight}
                            </span>
                            {' '}
                            <span className={css.dishCardRegularAdditionalInfoTextLineValue}>
                                {locales[locale].weight}
                            </span>
                        </div>
                    </div>
                    <div>
                        <div className={`body-l-semibold ${css.dishCardRegularDishTitle}`}>
                            {highlightWords.some((word: string) => Boolean(word))
                                ? highlightText(title, highlightWords, handleOpenDialog)
                                : tp.execute(title)}
                        </div>
                        <div className={`body-l-semibold ${css.dishCardRegularDishTitle} ${css.ellipsis}`}>
                            {highlightWords.some((word: string) => Boolean(word))
                                ? highlightText(caption, highlightWords, handleOpenDialog)
                                : tp.execute(caption)}
                        </div>
                    </div>
                    {isDishCardWithCustomTabBar && !isSoldOut && (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            role="none"
                        >
                            <TabBar
                                id={id}
                                selectedDishId={selectedDishId}
                                onNextIdSelected={onNextIdSelected}
                                ingredientsList={ingredientsList}
                            />
                        </div>
                    )}
                    {isDishCardWithCustomSelect && !isDishCardWithCustomTabBar && !isSoldOut && (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            role="none"
                        >
                            <CustomIngredientsSelectDishRow
                                id={id}
                                selectedDishId={selectedDishId}
                                onNextIdSelected={onNextIdSelected}
                                optionsList={ingredientsList}
                                onSubmitHandler={(arg) => { arg.onCompoleted(); }}
                                environmentContext="menu"
                                isNewLayout
                                titleRenderField="replaceableIngredient"
                            />
                        </div>
                    )}
                    {isDishWithAdditionalDishesSelect && !isDishCardWithCustomTabBar && !isDishCardWithCustomSelect && !isSoldOut && (
                        <div
                            onClick={(e) => e.stopPropagation()}
                            role="none"
                        >
                            <CustomIngredientsSelectDishRow
                                id={id}
                                selectedDishId={selectedDishId}
                                onNextIdSelected={onNextIdSelected}
                                optionsList={upsellDishesList}
                                onSubmitHandler={(arg) => { arg.onCompoleted(); }}
                                environmentContext="menu"
                                isNewLayout
                                titleRenderField="name_upsell"
                            />
                        </div>
                    )}
                    {currentOptionTags && currentOptionTags.length > 0 && <TagsShields tags={currentOptionTags} />}
                    {currentAdditionalDescription && (
                        <div
                            className={`body-xs-medium ${css.additionalDescription}`}
                            dangerouslySetInnerHTML={{ __html: addBrTag(currentAdditionalDescription) }}
                        />
                    )}
                </div>
            </div>
            <Portal>
                <DialogStaticNewUI
                    rejectText=""
                    confirmText="Понятно"
                    isOpen={isDialogOpenned}
                    onConfirm={() => {
                        setIsDialogOpenned(false);
                    }}
                    onReject={
                        () => {
                            setIsDialogOpenned(false);
                        }
                    }
                    textAlign="start"
                    isCloseConfirmAction={false}
                    // @ts-ignore
                    isNewIrresponsiveLayout
                    oneRowButtons
                    buttonsReversed
                    onlyConfirmButton
                >
                    <div className={css.dialogContent}>
                        {highlightImage && <img className={css.dialogImage} src={highlightImage} alt="ingredient" />}
                        <div className={`subheadings-sh3-medium ${css.dialogTitle}`}>
                            {highlightDescriptionTitle || highlightWords.map((word: string, index: number) => (
                                <span key={word}>
                                    {word}
                                </span>
                            ))}
                        </div>
                        <div className={`body-l-medium ${css.dialogDescription}`}>{highlightDescription}</div>
                    </div>
                </DialogStaticNewUI>
            </Portal>
        </>
    );
};
